import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, Shield, Building2, Loader2, AlertCircle } from 'lucide-react';

function Login({ setUser }) {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formData, setFormData] = useState({
    email: localStorage.getItem('office_suite_last_email') || '',
    password: ''
  });
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
  const [touched, setTouched] = useState({ email: false, password: false });
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const lockoutTimerRef = useRef(null);

  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    return '';
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    if (field === 'email') {
      setFieldErrors(prev => ({ ...prev, email: validateEmail(formData.email) }));
    } else if (field === 'password') {
      setFieldErrors(prev => ({ ...prev, password: validatePassword(formData.password) }));
    }
  };

  useEffect(() => {
    return () => {
      if (lockoutTimerRef.current) clearInterval(lockoutTimerRef.current);
    };
  }, []);

  const startLockout = (seconds) => {
    setLockoutSeconds(seconds);
    if (lockoutTimerRef.current) clearInterval(lockoutTimerRef.current);
    lockoutTimerRef.current = setInterval(() => {
      setLockoutSeconds(prev => {
        if (prev <= 1) {
          clearInterval(lockoutTimerRef.current);
          lockoutTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lockoutSeconds > 0) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('office_suite_last_email', formData.email);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      setUser(response.data.user);
      setFailedAttempts(0);
      const firstName = response.data.user?.first_name || response.data.user?.firstName || response.data.user?.name?.split(' ')[0];
      toast.success(firstName ? `Welcome back, ${firstName}!` : 'Welcome back!');
      navigate('/');
    } catch (error) {
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);
      if (newAttempts >= 3) {
        startLockout(30);
      }
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-[55%] bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Office Suite" className="w-14 h-14 rounded-xl object-contain" />
            <div>
              <h2 className="text-white font-bold text-xl tracking-tight">Office Suite</h2>
              <p className="text-slate-400 text-xs">Enterprise Resource Planning</p>
            </div>
          </div>
          <div className="max-w-lg">
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
              Supermarket, Bakery
              <span className="block text-emerald-400 mt-1">& Restaurant ERP</span>
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed mb-8">
              Unified platform for inventory, sales, production, HR, and financial operations across all your branches and departments.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Inventory & Sales', desc: 'Real-time stock & POS' },
                { label: 'Bakery & Production', desc: 'Recipes, bake logs, orders' },
                { label: 'HR & Payroll', desc: 'Complete workforce management' },
                { label: 'Multi-Branch', desc: 'Centralized operations' },
              ].map((feat, i) => (
                <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                  <p className="text-white font-semibold text-sm">{feat.label}</p>
                  <p className="text-slate-400 text-xs mt-1">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Shield className="w-4 h-4" />
            <span>256-bit SSL encrypted connection</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img src="/logo.png" alt="Office Suite" className="w-14 h-14 rounded-xl object-contain" />
            <div>
              <h2 className="text-slate-900 font-bold text-xl">Office Suite</h2>
              <p className="text-slate-500 text-xs">Enterprise Resource Planning</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200/60 p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900">Sign in</h2>
              <p className="text-slate-500 mt-1 text-sm">Access your enterprise dashboard</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input
                    data-testid="email-input"
                    type="email"
                    autoComplete="username"
                    aria-label="Email Address"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (touched.email) setFieldErrors(prev => ({ ...prev, email: validateEmail(e.target.value) }));
                    }}
                    onBlur={() => handleBlur('email')}
                    required
                    className={`w-full pl-11 pr-4 py-3 bg-slate-50 border rounded-xl text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:bg-white transition-all text-sm ${
                      touched.email && fieldErrors.email
                        ? 'border-red-400 focus:ring-red-500/20 focus:border-red-500'
                        : 'border-slate-200 focus:ring-emerald-500/20 focus:border-emerald-500'
                    }`}
                    placeholder="you@office-suite.com"
                  />
                </div>
                {touched.email && fieldErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input
                    data-testid="password-input"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    aria-label="Password"
                    value={formData.password}
                    onChange={(e) => {
                      setFormData({ ...formData, password: e.target.value });
                      if (touched.password) setFieldErrors(prev => ({ ...prev, password: validatePassword(e.target.value) }));
                    }}
                    onBlur={() => handleBlur('password')}
                    required
                    className={`w-full pl-11 pr-12 py-3 bg-slate-50 border rounded-xl text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:bg-white transition-all text-sm ${
                      touched.password && fieldErrors.password
                        ? 'border-red-400 focus:ring-red-500/20 focus:border-red-500'
                        : 'border-slate-200 focus:ring-emerald-500/20 focus:border-emerald-500'
                    }`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {touched.password && fieldErrors.password && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.password}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500/20 cursor-pointer"
                  />
                  <span className="text-sm text-slate-600">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={() => toast.info('Please contact your system administrator for password reset.')}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium hover:underline transition-colors"
                >
                  Forgot password?
                </button>
              </div>

              {lockoutSeconds > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-700">
                    Too many attempts. Please wait before trying again. ({lockoutSeconds}s)
                  </p>
                </div>
              )}

              <button
                data-testid="login-button"
                type="submit"
                disabled={loading || lockoutSeconds > 0}
                aria-busy={loading}
                className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 text-white py-3 px-4 rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/25 text-sm"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-100 text-center space-y-2">
              <p className="text-sm text-slate-500">
                First time?{' '}
                <Link to="/register" className="text-emerald-600 hover:text-emerald-700 font-medium hover:underline">
                  Set up your company
                </Link>
              </p>
              <p className="text-xs text-slate-400">
                Or contact your system administrator for an invitation.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mt-6">
            <span className="text-xs text-slate-400">Secure enterprise access</span>
            <span className="text-slate-300">|</span>
            <span className="text-xs text-slate-400">v3.0</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
